import type {
  WalletState,
  ChallengeResponse,
  VerifyResponse,
  SyncResponse,
} from "./walletTypes";
import { getSupabase } from "../auth/supabaseClient";

const EMPTY_STATE: WalletState = {
  address: null,
  isVerified: false,
  walletId: null,
  isBitbeastOwner: false,
  lastSyncAt: null,
  syncStatus: null,
  syncError: null,
  unlockedTokenIds: [],
  delegatedVaults: [],
  delegatedTokenMap: {},
};

export class WalletService {
  private state: WalletState = { ...EMPTY_STATE };

  /** Returns the current wallet state. */
  getState(): WalletState {
    return this.state;
  }

  /** AppKit handles wallet discovery (including mobile deep links). Always available. */
  isWalletAvailable(): boolean {
    return true;
  }

  /**
   * Returns shortened address like "0x1234...abcd" (first 6 + last 4 chars) or null.
   */
  getShortenedAddress(): string | null {
    const addr = this.state.address;
    if (!addr || addr.length < 10) return null;
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  }

  /**
   * Load cached wallet/NFT state from Supabase profiles + user_unlocked_avatars.
   * Called on login. Does NOT call Moralis.
   */
  async loadCachedState(userId: string): Promise<void> {
    try {
      const supabase = getSupabase();

      // Fetch wallet fields from profiles
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("primary_eth_wallet, is_bitbeast_owner, last_nft_sync_at, nft_sync_status, nft_sync_error")
        .eq("id", userId)
        .single();

      if (profileError || profile == null) {
        console.warn("[wallet] loadCachedState profile fetch failed:", profileError?.message ?? "no data");
        return;
      }

      const address = (profile.primary_eth_wallet as string | null) ?? null;

      // Fetch unlocked avatar token IDs
      let unlockedTokenIds: number[] = [];
      if (address) {
        // Find the wallet ID first
        const { data: wallet, error: walletError } = await supabase
          .from("user_wallets_eth")
          .select("id")
          .eq("user_id", userId)
          .eq("address", address.toLowerCase())
          .is("removed_at", null)
          .single();

        if (!walletError && wallet != null) {
          const { data: avatars } = await supabase
            .from("user_unlocked_avatars")
            .select("token_id, delegate_vault_address")
            .eq("source_wallet_id", wallet.id);

          if (avatars != null) {
            unlockedTokenIds = avatars.map((a: { token_id: number; delegate_vault_address: string | null }) => a.token_id);
          }

          // Build delegatedVaults (unique non-null vault addresses) and delegatedTokenMap
          const delegatedTokenMap: Record<string, number[]> = {};
          if (avatars != null) {
            for (const row of avatars as Array<{ token_id: number; delegate_vault_address: string | null }>) {
              if (row.delegate_vault_address != null) {
                const vault = row.delegate_vault_address.toLowerCase();
                if (delegatedTokenMap[vault] == null) {
                  delegatedTokenMap[vault] = [];
                }
                delegatedTokenMap[vault].push(row.token_id);
              }
            }
          }
          const delegatedVaults = Object.keys(delegatedTokenMap);

          this.state = {
            ...EMPTY_STATE,
            address: address.toLowerCase(),
            isVerified: true,
            walletId: wallet.id as string,
            isBitbeastOwner: (profile.is_bitbeast_owner as boolean | null) ?? false,
            lastSyncAt: (profile.last_nft_sync_at as string | null) ?? null,
            syncStatus: ((profile.nft_sync_status as string | null) ?? null) as WalletState["syncStatus"],
            syncError: (profile.nft_sync_error as string | null) ?? null,
            unlockedTokenIds,
            delegatedVaults,
            delegatedTokenMap,
          };
          return;
        }
      }

      // No verified wallet linked
      this.state = { ...EMPTY_STATE };
    } catch (err) {
      console.warn("[wallet] loadCachedState error:", err instanceof Error ? err.message : String(err));
    }
  }

  /**
   * Full wallet connect flow via AppKit provider:
   * 1. Get EVM provider from AppKit
   * 2. Get SIWE challenge from wallet-challenge Edge Function
   * 3. Sign the message using ethers.BrowserProvider
   * 4. Verify signature via wallet-verify Edge Function
   * 5. Update internal state on success
   */
  async connect(): Promise<VerifyResponse> {
    const { getEthProvider } = await import("./appKitService");
    const rawProvider = getEthProvider();
    if (rawProvider == null) {
      return { success: false, error: "No EVM provider — connect wallet via AppKit first" };
    }
    return this._connectWithProvider(rawProvider);
  }

  /**
   * Connect using a provider received from subscribeProviders callback.
   * Avoids redundant getEthProvider() call when AppKit auto-reconnects.
   */
  async connectFromProvider(rawProvider: unknown): Promise<VerifyResponse> {
    if (rawProvider == null) {
      return { success: false, error: "No provider supplied" };
    }
    return this._connectWithProvider(rawProvider);
  }

  /** Reset state on wallet disconnect (called by main.ts when subscribeProviders fires). */
  onDisconnect(): void {
    this.state = { ...EMPTY_STATE };
  }

  private async _connectWithProvider(rawProvider: unknown): Promise<VerifyResponse> {
    try {
      const { BrowserProvider } = await import("ethers");
      const provider = new BrowserProvider(rawProvider as any);
      const signer = await provider.getSigner();
      const address = (await signer.getAddress()).toLowerCase();

      // Get challenge
      const supabase = getSupabase();
      const { data: challengeData, error: challengeError } = await supabase.functions.invoke("wallet-challenge", {
        body: { address },
      });

      if (challengeError || challengeData == null) {
        const msg = challengeError?.message ?? "Failed to get challenge from server.";
        return { success: false, error: msg };
      }

      const challenge = challengeData as ChallengeResponse;

      // Sign message
      let signature: string;
      try {
        signature = await signer.signMessage(challenge.messageToSign);
      } catch (signErr) {
        const msg = signErr instanceof Error ? signErr.message : String(signErr);
        if (msg.toLowerCase().includes("rejected") || msg.toLowerCase().includes("denied") || msg.toLowerCase().includes("cancel")) {
          return { success: false, error: "Signature request rejected." };
        }
        return { success: false, error: `Signing failed: ${msg}` };
      }

      // Verify signature
      const { data: verifyData, error: verifyError } = await supabase.functions.invoke("wallet-verify", {
        body: { address, signature, nonceId: challenge.nonceId },
      });

      if (verifyError || verifyData == null) {
        const msg = verifyError?.message ?? "Wallet verification failed.";
        return { success: false, error: msg };
      }

      const verifyResp = verifyData as VerifyResponse;

      if (!verifyResp.success) {
        return { success: false, error: verifyResp.error ?? "Verification failed." };
      }

      // Update state
      this.state = {
        address,
        isVerified: true,
        walletId: verifyResp.walletId ?? null,
        isBitbeastOwner: false,
        lastSyncAt: new Date().toISOString(),
        syncStatus: "ok",
        syncError: null,
        unlockedTokenIds: verifyResp.tokenIds ?? [],
        delegatedVaults: [],
        delegatedTokenMap: {},
      };

      await this._refreshBitbeastOwnerStatus();

      return { success: true, walletId: verifyResp.walletId, tokenIds: verifyResp.tokenIds };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn("[wallet] connect error:", msg);
      return { success: false, error: msg };
    }
  }

  /**
   * Trigger manual NFT sync via nft-sync Edge Function.
   * Updates internal state on success/failure.
   */
  async syncNfts(auto: boolean = false): Promise<SyncResponse> {
    if (!this.state.address) {
      return { success: false, error: "No wallet connected." };
    }

    try {
      const supabase = getSupabase();
      const { data, error } = await supabase.functions.invoke("nft-sync", {
        body: { auto },
      });

      if (error || data == null) {
        const msg = error?.message ?? "NFT sync failed.";
        this.state = { ...this.state, syncStatus: "failed", syncError: msg };
        return { success: false, error: msg };
      }

      const resp = data as SyncResponse;

      this.state = {
        ...this.state,
        lastSyncAt: new Date().toISOString(),
        syncStatus: resp.syncStatus as WalletState["syncStatus"] ?? "ok",
        syncError: null,
        unlockedTokenIds: resp.tokenIds ?? this.state.unlockedTokenIds,
        delegatedVaults: resp.delegatedVaults ?? this.state.delegatedVaults,
      };

      return resp;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn("[wallet] syncNfts error:", msg);
      this.state = { ...this.state, syncStatus: "failed", syncError: msg };
      return { success: false, error: msg };
    }
  }

  /**
   * Unlink the connected wallet:
   * - Sets removed_at on user_wallets_eth
   * - Clears profiles.primary_eth_wallet
   * - Removes user_unlocked_avatars for this wallet
   * - Resets local state
   */
  async unlink(): Promise<boolean> {
    const { address, walletId } = this.state;
    if (!address || !walletId) {
      // Nothing to unlink
      this.state = { ...EMPTY_STATE };
      return true;
    }

    try {
      const supabase = getSupabase();

      // Mark wallet as removed
      const { error: walletError } = await supabase
        .from("user_wallets_eth")
        .update({ removed_at: new Date().toISOString() })
        .eq("id", walletId);

      if (walletError) {
        console.warn("[wallet] unlink wallet update error:", walletError.message);
      }

      // Clear primary_eth_wallet from profiles (use current user's session)
      const { data: { user } } = await supabase.auth.getUser();
      if (user != null) {
        const { error: profileError } = await supabase
          .from("profiles")
          .update({ primary_eth_wallet: null })
          .eq("id", user.id);

        if (profileError) {
          console.warn("[wallet] unlink profile update error:", profileError.message);
        }
      }

      // Remove unlocked avatars for this wallet
      const { error: avatarError } = await supabase
        .from("user_unlocked_avatars")
        .delete()
        .eq("source_wallet_id", walletId);

      if (avatarError) {
        console.warn("[wallet] unlink avatar delete error:", avatarError.message);
      }

      this.state = { ...EMPTY_STATE };
      return true;
    } catch (err) {
      console.warn("[wallet] unlink error:", err instanceof Error ? err.message : String(err));
      this.state = { ...EMPTY_STATE };
      return false;
    }
  }

  /** Refresh isBitbeastOwner from the profiles table after connecting. */
  private async _refreshBitbeastOwnerStatus(): Promise<void> {
    try {
      const supabase = getSupabase();
      const { data: { user } } = await supabase.auth.getUser();
      if (user == null) return;

      const { data } = await supabase
        .from("profiles")
        .select("is_bitbeast_owner")
        .eq("id", user.id)
        .single();

      if (data != null) {
        this.state = { ...this.state, isBitbeastOwner: (data.is_bitbeast_owner as boolean | null) ?? false };
      }
    } catch { /* non-critical */ }
  }
}

export const walletService = new WalletService();
